import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { CheckCircle, XCircle, Clock } from 'lucide-react';
import {
  NO_LONGER_AVAILABLE_ERROR_CODE,
  type TokenErrorCode,
} from '~/lib/magicLinkToken';

export function MagicLinkStatusCard({
  testId,
  icon,
  title,
  message,
  cta,
}: {
  testId: string;
  icon: ReactNode;
  title: string;
  message: string;
  cta?: ReactNode;
}) {
  return (
    <div
      className="min-h-screen flex items-center justify-center bg-neut-bg-alt p-4 w-full"
      data-testid={testId}
    >
      <div className="bg-neut-bg-alt rounded-xl shadow-md border border-neut-border max-w-md w-full p-8">
        <div className="flex flex-col items-center gap-4 text-center">
          {icon}
          <h1 className="text-xl font-bold text-neut-prim">{title}</h1>
          <p className="text-sm text-neut-sec">{message}</p>
          {cta}
        </div>
      </div>
    </div>
  );
}

const ERROR_ICONS: Record<TokenErrorCode, ReactNode> = {
  invalid: <XCircle className="text-error-500" size={48} />,
  expired: <Clock className="text-warning-700" size={48} />,
  'already-responded': <CheckCircle className="text-prim" size={48} />,
  'already-used': <CheckCircle className="text-prim" size={48} />,
  [NO_LONGER_AVAILABLE_ERROR_CODE]: (
    <XCircle className="text-error-500" size={48} />
  ),
  unknown: <XCircle className="text-error-500" size={48} />,
};

const ERROR_KEY_SUFFIX: Record<TokenErrorCode, string> = {
  invalid: 'invalid',
  expired: 'expired',
  'already-responded': 'already_responded',
  'already-used': 'already_used',
  [NO_LONGER_AVAILABLE_ERROR_CODE]: 'no_longer_available',
  unknown: 'unknown',
};

export function TokenErrorView({
  code,
  namespace,
  keyPrefix,
  testId,
}: {
  code: TokenErrorCode;
  namespace: string;
  keyPrefix: string;
  testId: string;
}) {
  const { t } = useTranslation(namespace);
  const suffix = ERROR_KEY_SUFFIX[code];
  return (
    <MagicLinkStatusCard
      testId={testId}
      icon={ERROR_ICONS[code]}
      title={t(`${keyPrefix}.error.${suffix}.title`)}
      message={t(`${keyPrefix}.error.${suffix}.message`)}
      cta={
        code === 'unknown' && (
          <Link
            to="/"
            className="mt-2 text-sm font-medium text-prim hover:underline"
            data-testid="error-cta-link"
          >
            {t(`${keyPrefix}.error.unknown.cta`)}
          </Link>
        )
      }
    />
  );
}
